import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SideListModel } from '@app/core/Models';
import { NotificationService } from '@app/core/Services';
import {
  DeleteAutoRoundingSetting,
  GetAutoRoundingCompanySetting,
  GetAutoRoundingSettingAccounts,
  GetAutoRoundingSettingList,
  GetFundNameList,
  UpdateAutoRoundingCompanySetting,
} from '@app/core/Store';
import { Store } from '@ngxs/store';
import { ConfirmationBoxComponent } from '@app/modules/common';
import {
  AddRoundingFigureComponent,
} from './add-rounding-figure/add-rounding-figure.component';
import { NotificationDetails, NotificationHeader } from '@app/core/Enum/notification';
import { ConfirmationType, NotificationTextMessage } from '@app/core/Enum';

interface RoundingFigureRow {
  id: string;
  profitLossAccountId: any;
  profitLossAccountName: string;
  balanceSheetAccountId: any;
  balanceSheetAccountName: string;
  fundId: any;
  fundName: string;
  periodName: string;
}

interface AccountGroupModel {
  groupName: string;
  groupId: number;
  typeId: number;
  listModels: SideListModel[];
}

@Component({
  selector: 'app-rounding-figures-accounts',
  templateUrl: './rounding-figures-accounts.component.html',
  styleUrls: ['./rounding-figures-accounts.component.scss'],
})
export class RoundingFiguresAccountsComponent implements OnInit {
  roundingFigureForm: FormGroup;

  profitLossAccounts: AccountGroupModel[] = [];
  balanceSheetAccounts: AccountGroupModel[] = [];
  fundList: SideListModel[] = [];

  dataSource: RoundingFigureRow[] = [];
  displayedColumns: string[] = [
    'profitLossAccount',
    'balanceSheetAccount',
    'fund',
    'period',
    'action',
  ];

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private notifier: NotificationService
  ) {}

  ngOnInit(): void {
    this.roundingFigureForm = new FormGroup({
      manageAutoRoundingValues: new FormControl(false),
    });

    this.loadAccountsFromApi();
    this.getFundList();
    this.loadRoundingSettingList();
    this.loadAutoRoundingCompanySetting();
  }

  onManageAutoRoundingChange(isChecked: boolean): void {
    this.store.dispatch(new UpdateAutoRoundingCompanySetting(isChecked)).subscribe({
      error: () => {
        this.roundingFigureForm.patchValue(
          { manageAutoRoundingValues: !isChecked },
          { emitEvent: false }
        );
      },
    });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(AddRoundingFigureComponent, {      
      disableClose: true,
      panelClass: 'round-dialog',
      data: {
        profitLossAccounts: this.profitLossAccounts,
        balanceSheetAccounts: this.balanceSheetAccounts,
        fundList: this.fundList,
      },
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.loadRoundingSettingList();
      }
    });
  }

  editRow(row: RoundingFigureRow): void {
    const dialogRef = this.dialog.open(AddRoundingFigureComponent, {
      panelClass: 'round-dialog',
      disableClose: true,
      data: {
        id: row.id,
        profitLossAccounts: this.profitLossAccounts,
        balanceSheetAccounts: this.balanceSheetAccounts,
        fundList: this.fundList,
      },
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.loadRoundingSettingList();
      }
    });
  }

  deleteRow(row: RoundingFigureRow): void {
    this.dialog
      .open(ConfirmationBoxComponent, {
        disableClose: true,
        data: {
          type: ConfirmationType.Save,
          headerText: NotificationHeader.deleteConfirmation,
          detailText: NotificationDetails.deleteAllDetailText,
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.store
            .dispatch(new DeleteAutoRoundingSetting(row.id))
            .subscribe(() => {
              this.loadRoundingSettingList();
              this.notifier.success(
                NotificationHeader.success,
                NotificationTextMessage.recordDeletedSuccessfully
              );
            });
        }
      });
  }

  private loadAccountsFromApi(): void {
    this.store
      .dispatch(new GetAutoRoundingSettingAccounts())
      .subscribe((res) => {
        const data = res.accountProduction?.autoRoundingSettingAccounts;
        if (data) {
          this.profitLossAccounts = data.profitLossAccounts || [];
          this.balanceSheetAccounts = data.balanceSheetAccounts || [];
        }
      });
  }

  private loadRoundingSettingList(): void {
    this.store
      .dispatch(new GetAutoRoundingSettingList())
      .subscribe((res) => {
        const list: any[] = res.accountProduction?.autoRoundingSettingList || [];
        this.dataSource = list.map((item) => ({
          id: item.id,
          profitLossAccountId: item.profitLossRoundingAccountId,
          profitLossAccountName: item.profitLossRoundingAccountName,
          balanceSheetAccountId: item.balanceSheetRoundingAccountId,
          balanceSheetAccountName: item.balanceSheetRoundingAccountName,
          fundId: item.fundRoundingAccountId,
          fundName: item.fundRoundingAccountName,
          periodName: item.accountingPeriodName,
        }));
      });
  }

  private getFundList(): void {
    this.store.dispatch(new GetFundNameList()).subscribe((res) => {
      this.fundList = res.account?.fundNameList || [];
    });
  }

  private loadAutoRoundingCompanySetting(): void {
    this.store.dispatch(new GetAutoRoundingCompanySetting()).subscribe((res) => {
      const apiValue = res.accountProduction?.autoRoundingCompanySetting;
      const isChecked = apiValue?.isAutoRounding ?? false;

      this.roundingFigureForm.patchValue(
        { manageAutoRoundingValues: isChecked },
        { emitEvent: false }
      );
    });
  }
}
